import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GridApiPremium } from "@mui/x-data-grid-premium";
import _ from "lodash";

interface ColumnDefinition {
  name: string; // Should be in dot-notation format (e.g., "supply.listed")
  type: string;
}

interface PrependFormProps {
  columnNames: ColumnDefinition[];
  closeModal: () => void;
  apiRef: React.RefObject<GridApiPremium>;
  getSelectedRows: () => any[];
  setSnackbar?: (snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error";
  }) => void;
}

/**
 * Formats a field name for display (e.g., "supply.inventory_notes" -> "Inventory Notes")
 */
const formatLabel = (name: string): string => {
  // Extract the column name from dot-notation (e.g., "supply.listed" -> "listed")
  const columnName = name.includes(".") ? name.split(".")[1] : name;
  const words = columnName.split("_");
  return words
    .map((word) => {
      if (word === "amz") return "Amazon";
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

/**
 * Returns a cleaned, prepended string value.
 * Removes the specified text, then prepends the new text.
 */
const newValString = (
  currVal: string,
  valToPrepend: string,
  valToRemove: string
): string => {
  const cleanedField = currVal.replace(valToRemove, "");
  return valToPrepend.concat(` ${cleanedField}`).replace(/\s\s+/g, " ").trim();
};

export function PrependForm({
  columnNames,
  closeModal,
  apiRef,
  getSelectedRows,
  setSnackbar,
}: PrependFormProps) {
  // Build initial values and validation schema dynamically
  // Use underscore instead of dot to avoid Formik's nested path interpretation
  const initialValues: Record<string, string> = {};
  const validationSchema: Record<string, any> = {};

  columnNames.forEach((col) => {
    // Replace dots with underscores for form field keys
    const fieldKey = col.name.replace(/\./g, "_");

    initialValues[`prepend_to_${fieldKey}`] = "";
    initialValues[`remove_from_${fieldKey}`] = "";

    validationSchema[`prepend_to_${fieldKey}`] =
      col.type === "string"
        ? Yup.string()
        : Yup.number().typeError("A number is required");
    validationSchema[`remove_from_${fieldKey}`] =
      col.type === "string"
        ? Yup.string()
        : Yup.number().typeError("A number is required");
  });

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape(validationSchema),
    onSubmit: async (values) => {
      const selectedRows = getSelectedRows();

      if (selectedRows.length === 0) {
        if (setSnackbar) {
          setSnackbar({
            open: true,
            message: "Please select at least one row",
            severity: "error",
          });
        }
        return;
      }

      try {
        // Process each selected row
        for (const row of selectedRows) {
          const updatedRow = _.cloneDeep(row);

          // Apply prepend/remove logic for each column
          columnNames.forEach((col) => {
            // Convert dot-notation to underscore for form field key lookup
            const fieldKey = col.name.replace(/\./g, "_");
            const prependValue = values[`prepend_to_${fieldKey}`] || "";
            const removeValue = values[`remove_from_${fieldKey}`] || "";

            // Parse dot-notation column name (e.g., "supply.listed" -> ["supply", "listed"])
            const parts = col.name.split(".");
            const table = parts[0];
            const columnName = parts.slice(1).join(".");

            // Get current value from nested structure
            let currentValue = row[table]?.[columnName];
            if (currentValue === null || currentValue === undefined) {
              currentValue = "";
            }

            // Calculate new value
            let newValue: string | number;
            if (col.type === "string") {
              newValue = newValString(
                String(currentValue),
                prependValue,
                removeValue
              );
            } else {
              // For numbers, just use the prepend value if provided
              newValue = prependValue ? Number(prependValue) : currentValue;
            }

            // Update using dot-notation format for proper change detection
            updatedRow[col.name] = newValue;
          });

          // Update the row via the dataSource to send server update
          await apiRef.current.dataSource.editRow({
            rowId: row.id,
            updatedRow: updatedRow,
            previousRow: row,
          });
        }

        // Success notification handled by dataSource.updateRow
        closeModal();
      } catch (error) {
        console.error("Error updating rows:", error);
        // Error notification already handled by dataSource.updateRow
      }
    },
  });

  return (
    <Dialog open={true} onClose={closeModal} maxWidth="sm" fullWidth>
      <DialogTitle>Prepend to Field</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          {columnNames.map((col) => {
            // Convert dot-notation to underscore for form field keys
            const fieldKey = col.name.replace(/\./g, "_");
            return (
              <Box key={col.name}>
                <TextField
                  fullWidth
                  id={`prepend_to_${fieldKey}`}
                  name={`prepend_to_${fieldKey}`}
                  label={`Prepend to ${formatLabel(col.name)}`}
                  value={formik.values[`prepend_to_${fieldKey}`]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched[`prepend_to_${fieldKey}`] &&
                    Boolean(formik.errors[`prepend_to_${fieldKey}`])
                  }
                  helperText={
                    formik.touched[`prepend_to_${fieldKey}`] &&
                    formik.errors[`prepend_to_${fieldKey}`]
                  }
                  margin="dense"
                />
                <TextField
                  fullWidth
                  id={`remove_from_${fieldKey}`}
                  name={`remove_from_${fieldKey}`}
                  label={`Remove from ${formatLabel(col.name)}`}
                  value={formik.values[`remove_from_${fieldKey}`]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched[`remove_from_${fieldKey}`] &&
                    Boolean(formik.errors[`remove_from_${fieldKey}`])
                  }
                  helperText={
                    formik.touched[`remove_from_${fieldKey}`] &&
                    formik.errors[`remove_from_${fieldKey}`]
                  }
                  margin="dense"
                />
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Cancel</Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          color="primary"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
